import LinkOffIcon from '@mui/icons-material/LinkOff'
import { Alert, Box, Button, CircularProgress, Link as MuiLink, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import AuthShell from './AuthShell'

interface ResetPasswordProps {
  token?: string
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ token }) => {
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirm: '',
  })
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const translate = useTranslate()

  // Extract token from URL if not provided as prop
  useEffect(() => {
    if (!token) {
      const urlParams = new URLSearchParams(window.location.search)
      const urlToken = urlParams.get('token')
      if (!urlToken) {
        setIsError(true)
        setMessage(translate('pos.auth.reset_password.invalid_link_message'))
      }
    }
  }, [token, translate])

  const actualToken = token || new URLSearchParams(window.location.search).get('token')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!actualToken) {
      setIsError(true)
      setMessage(translate('pos.auth.reset_password.invalid_link_message'))
      return
    }

    if (formData.password !== formData.passwordConfirm) {
      setIsError(true)
      setMessage(translate('pos.auth.reset_password.passwords_dont_match'))
      return
    }

    if (formData.password.length < 1) {
      setIsError(true)
      setMessage(translate('pos.auth.reset_password.password_required'))
      return
    }

    setIsLoading(true)

    try {
      const res = await deadlineFetch('/api/users/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: actualToken,
          password: formData.password,
        }),
      })

      const data = await res.json()

      if (res.ok) {
        setIsError(false)
        setMessage(translate('pos.auth.reset_password.success'))
        setTimeout(() => {
          window.location.href = '/vet-portal/#/login'
        }, 2000)
      } else {
        setIsError(true)
        if (res.status === 403) {
          setMessage(translate('pos.auth.reset_password.invalid_link_message'))
        } else if (res.status === 400) {
          setMessage(data.message || translate('pos.auth.reset_password.invalid_request'))
        } else {
          setMessage(data.message || translate('pos.auth.reset_password.reset_failed'))
        }
      }
    } catch (error) {
      setIsError(true)
      setMessage(translate('pos.auth.reset_password.network_error'))
    } finally {
      setIsLoading(false)
    }
  }

  const backToLogin = (
    <MuiLink
      component="button"
      variant="body2"
      underline="hover"
      onClick={(e) => {
        e.preventDefault()
        window.location.href = '/vet-portal/#/login'
      }}
    >
      {translate('pos.auth.forgot_password.back_to_login')}
    </MuiLink>
  )

  if (!actualToken) {
    return (
      <AuthShell
        icon={<LinkOffIcon sx={{ fontSize: 40, color: 'text.secondary' }} />}
        title={translate('pos.auth.reset_password.invalid_link_title')}
        subtitle={translate('pos.auth.reset_password.invalid_link_message')}
        footer={backToLogin}
      >
        <Button
          variant="contained"
          fullWidth
          sx={{ py: 1.25 }}
          onClick={() => (window.location.href = '/vet-portal/#/forgot-password')}
        >
          {translate('pos.auth.reset_password.request_new')}
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={translate('pos.auth.reset_password.title')} footer={backToLogin}>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <TextField
          fullWidth
          label={translate('pos.auth.reset_password.new_password_label')}
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          disabled={isLoading}
          sx={{ mb: 2 }}
          autoComplete="new-password"
        />

        <TextField
          fullWidth
          label={translate('pos.auth.reset_password.confirm_password_label')}
          type="password"
          name="passwordConfirm"
          value={formData.passwordConfirm}
          onChange={handleChange}
          required
          disabled={isLoading}
          sx={{ mb: 2 }}
          autoComplete="new-password"
        />

        {message && (
          <Alert
            severity={isError ? 'error' : 'success'}
            sx={{ mb: 2 }}
            action={
              isError && message === translate('pos.auth.reset_password.invalid_link_message') ? (
                <MuiLink
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={(e) => {
                    e.preventDefault()
                    window.location.href = '/vet-portal/#/forgot-password'
                  }}
                  sx={{ color: 'inherit' }}
                >
                  {translate('pos.auth.reset_password.request_new')}
                </MuiLink>
              ) : undefined
            }
          >
            {message}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{ py: 1.25 }}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {isLoading
            ? translate('pos.auth.reset_password.resetting')
            : translate('pos.auth.reset_password.reset_button')}
        </Button>
      </Box>
    </AuthShell>
  )
}

export default ResetPassword
